import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import {
  returnsPolicyApi,
  type StoreConfigReturnPolicy,
  type UpdateReturnPolicyPayload,
} from '../api/returnsPolicyApi';

const KEY = 'returns-policy';

const errMsg = (err: unknown, fallback: string) => (err as Error)?.message || fallback;

/** Merchant's own policy. Only a MERCHANT-scoped session can resolve `me`. */
export const useMyReturnPolicy = (enabled = true) =>
  useQuery<StoreConfigReturnPolicy>({
    queryKey: [KEY, 'me'],
    queryFn: returnsPolicyApi.getMine,
    enabled,
  });

/** TCS admin read-only view for one merchant. */
export const useMerchantReturnPolicy = (merchantId: string | null | undefined) =>
  useQuery<StoreConfigReturnPolicy>({
    queryKey: [KEY, 'merchant', merchantId],
    queryFn: () => returnsPolicyApi.getForMerchant(merchantId as string),
    enabled: !!merchantId,
  });

export const useUpdateMyReturnPolicy = () => {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: UpdateReturnPolicyPayload) => returnsPolicyApi.updateMine(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      toast.success('Returns policy saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save returns policy')),
  });
};
